import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Space, 
  message, 
  Spin, 
  Alert,
  Tabs,
  Divider,
  Modal
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined, 
  PrinterOutlined, 
  EditOutlined
} from '@ant-design/icons';
import { useTemplate } from '../../hooks/templateHooks';
import { createTemplate } from '../../models/templateAPI';
import TemplateDesigner from './TemplateDesigner';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const TemplateEditor = ({ 
  templateId, 
  onCancel, 
  onSave, 
  onChangesDetected, 
  onChangesSaved 
}) => {
  const isNewTemplate = !templateId;
  const { template, loading, error, fetchTemplate, updateTemplate } = useTemplate(isNewTemplate ? null : templateId);
  
  const [form] = Form.useForm();
  const [elements, setElements] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [activeKey, setActiveKey] = useState('1');
  
  // Initialize form when template is loaded
  useEffect(() => {
    if (template) {
      form.setFieldsValue({
        name: template.name,
        description: template.description || '',
      });
      setElements(template.data || []);
    }
  }, [template, form]);
  
  // Handle changes to form or elements
  const handleValuesChange = () => {
    if (onChangesDetected) {
      onChangesDetected();
    }
  };
  
  // Handle elements change
  const handleElementsChange = (newElements) => {
    setElements(newElements);
    if (onChangesDetected) {
      onChangesDetected();
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      setIsSaving(true);
      
      try {
        if (isNewTemplate) {
          await createTemplate({
            name: values.name,
            description: values.description || undefined,
            data: elements,
          });
          
          message.success('Template created successfully');
          if (onChangesSaved) onChangesSaved();
          if (onSave) onSave();
        } else {
          await updateTemplate({
            name: values.name,
            description: values.description || undefined,
            data: elements,
          });
          
          message.success('Template updated successfully');
          if (onChangesSaved) onChangesSaved();
          if (onSave) onSave();
        }
      } catch (err) {
        message.error(`Failed to save template: ${err.message}`);
      } finally {
        setIsSaving(false);
      }
    } catch (validationError) {
      // Form validation failed
      if (activeKey !== '1') {
        setActiveKey('1');
        message.error('Please complete the required fields');
      }
    }
  };
  
  if (!isNewTemplate && loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="Loading template..." />
      </div>
    );
  }
  
  if (!isNewTemplate && error) {
    return (
      <Alert 
        message="Error Loading Template" 
        description={error} 
        type="error" 
        showIcon 
        action={
          <Space>
            <Button size="small" onClick={onCancel}>
              Back to Templates
            </Button>
            <Button size="small" type="primary" onClick={() => fetchTemplate()}>
              Retry
            </Button>
          </Space>
        }
      />
    );
  }
  
  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {isNewTemplate ? 'Create New Template' : `Edit Template: ${template?.name}`}
            </Title>
            {!isNewTemplate && (
              <Text type="secondary">
                Last updated: {new Date(template?.updatedAt).toLocaleString()}
              </Text>
            )}
          </div>
          
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={onCancel}
            >
              Back to Templates
            </Button>
            
            <PermissionButton permissionModule="templates" permissionAction={isNewTemplate ? 'create' : 'update'}
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={handleSubmit}
              loading={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Template'}
            </PermissionButton>
          </Space>
        </div>
        
        <Tabs activeKey={activeKey} onChange={setActiveKey} type="card">
          <TabPane 
            tab={
              <span>
                <EditOutlined />
                Template Details
              </span>
            } 
            key="1"
          >
            <Form 
              form={form} 
              layout="vertical" 
              onValuesChange={handleValuesChange}
              style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 0' }}
            >
              <Form.Item
                name="name"
                label="Template Name"
                rules={[{ required: true, message: 'Please enter a template name' }]}
              >
                <Input placeholder="Enter a descriptive name for your template" />
              </Form.Item>
              
              <Form.Item
                name="description"
                label="Description (optional)"
              >
                <TextArea 
                  rows={4} 
                  placeholder="Enter a description to help identify this template"
                />
              </Form.Item>
              
              <Divider>Template Design</Divider>
              
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Paragraph>
                  Switch to the Designer tab to create your template layout
                </Paragraph>
                <Button type="primary" onClick={() => setActiveKey('2')}>
                  Open Designer
                </Button>
              </div>
            </Form>
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <EditOutlined />
                Template Designer
              </span>
            } 
            key="2"
          >
            <TemplateDesigner
              initialElements={elements}
              onChange={handleElementsChange}
              onSave={handleSubmit}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default TemplateEditor;